import type { ElementType } from 'react';
import type { TFunction } from 'i18next';
import { Building2, Warehouse } from 'lucide-react';

import type { AddressComplementPart, FormattedMeasurement } from '@klareospace/pharos-lib-front-utils';
import type { Computable, SiteSummary } from '@klareospace/pharos-lib-types';
import { joinStrings } from '@klareospace/pharos-lib-utils';
import { resolveSiteTypeIcon } from '@klareospace/pharos-lib-web-utils';

// ─── Value Formatters ─────────────────────────────────────────────────────────

/**
 * Format a FormattedMeasurement result for display.
 * Returns '—' (em dash) when the result is null (measurement was undefined).
 *
 * @example formatMeasurementText({ isComputed: false, text: '5.2 kg' })  // '5.2 kg'
 * @example formatMeasurementText(null)                                    // '—'
 */
export function formatMeasurementText(result: FormattedMeasurement | null): string {
  if (result === null) return '—';
  return result.text;
}

/**
 * Format an array of tags for display as hash-prefixed uppercase tokens.
 * Returns '—' (em dash) when tags is undefined or empty.
 *
 * @example formatTags(['fresh', 'fragile'])  // '#FRESH #FRAGILE'
 * @example formatTags(['express'])           // '#EXPRESS'
 * @example formatTags([])                   // '—'
 * @example formatTags(undefined)            // '—'
 */
export function formatTags(tags: string[] | undefined): string {
  if (tags === undefined || tags.length === 0) return '—';
  return joinStrings(
    tags.map((tag) => `#${tag.toUpperCase()}`),
    ' ',
  );
}

/**
 * Format an order instruction for display, preserving line breaks (the caller renders it with
 * `whitespace-pre-wrap`). Returns '—' (em dash) when absent or blank.
 *
 * @example formatInstruction('Ring the bell twice')  // 'Ring the bell twice'
 * @example formatInstruction(undefined)               // '—'
 * @example formatInstruction('   ')                   // '—'
 */
export function formatInstruction(instruction: string | null | undefined): string {
  if (instruction == null || instruction.trim().length === 0) return '—';
  return instruction;
}

/**
 * Format a Computable<number> handling-unit quantity for display.
 * Returns '—' (em dash) when the value is undefined.
 *
 * @example formatHandlingUnitQuantity({ data: 5, isComputed: false })  // '5'
 * @example formatHandlingUnitQuantity(undefined)                        // '—'
 */
export function formatHandlingUnitQuantity(qty: Computable<number> | undefined): string {
  if (qty === undefined) return '—';
  return qty.data.toString();
}

/**
 * Format a single address complement part for display (digicode, elevator, floor, complement,
 * additional info). Uses fully-qualified `orders:drawer.*` keys so it resolves regardless of the
 * caller's default i18next namespace — shared by the order detail drawer and the task detail popin.
 */
export function formatAddressComplementPart(part: AddressComplementPart, t: TFunction): string {
  switch (part.type) {
    case 'additionalInfo':
    case 'complement':
      return part.value;
    case 'digicode':
      return t('orders:drawer.digicode', { value: part.value });
    case 'elevator':
      return part.hasElevator ? t('orders:drawer.elevator') : t('orders:drawer.noElevator');
    case 'floor':
      return t('orders:drawer.floor', { value: part.value });
  }
}

// ─── Icon Resolvers ───────────────────────────────────────────────────────────

/**
 * Resolve the icon component for an order delivery site.
 * Returns Building2 as the fallback when no site is configured or the type
 * is not in SITE_TYPE_ICON_MAP.
 */
export function resolveDeliveryIcon(site: SiteSummary | undefined): ElementType {
  return resolveSiteTypeIcon(site?.type, Building2);
}

/**
 * Resolve the icon component for an order pickup site.
 * Returns Warehouse as the fallback when no site is configured or the type
 * is not in SITE_TYPE_ICON_MAP.
 */
export function resolvePickupIcon(site: SiteSummary | undefined): ElementType {
  return resolveSiteTypeIcon(site?.type, Warehouse);
}
